"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { ArrowRight, BookOpen, Calendar, Clock, FileText } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const fallbackArticles = [{
  id: "fb-1",
  title: "ASTM E2016 Explained: Standard Specification for Industrial Woven Wire Cloth",
  excerpt: "Comprehensive breakdown of ASTM E2016 requirements, aperture tolerances, wire diameter allowances, and compliance verification standards.",
  category: "Compliance & Standards",
  date: "October 14, 2024",
  readTime: "6 min read",
  link: "/AstmE2016ExplainedPage",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/astm_e2016_guide.webp",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/astm_e2016_guide.webp-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/astm_e2016_guide.webp-wvc-sizes"
}, {
  id: "fb-2",
  title: "ISO Wire Mesh Standards: International Aperture & Tolerances Guide",
  excerpt: "Technical analysis comparing ISO 9044 industrial wire cloth parameters with North American and European mesh sieve specifications.",
  category: "ISO Standards",
  date: "November 02, 2024",
  readTime: "8 min read",
  link: "/ISOWireMeshStandardsPage",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/iso_wire_mesh_standards.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/iso_wire_mesh_standards.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/iso_wire_mesh_standards.png-wvc-sizes"
}, {
  id: "fb-3",
  title: "Stainless Steel Material Verification & Mill Test Report Inspection",
  excerpt: "Best practices for auditing alloy composition (304, 316L, 904L, Hastelloy) and chemical verification in high-corrosion filtration mesh.",
  category: "Material Testing",
  date: "December 18, 2024",
  readTime: "5 min read",
  link: "/StainlessSteelMaterialVerificationPage",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/material_verification_guide.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/material_verification_guide.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/material_verification_guide.png-wvc-sizes"
}];
function RelatedArticlesConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
        {[0, 1, 2].map(i => <div key={i} className="h-96 rounded-lg border border-border bg-card/50 p-6 flex flex-col justify-between">
            <div className="space-y-4">
              <div className="h-40 rounded-lg bg-muted" />
              <div className="h-4 w-24 rounded bg-muted" />
              <div className="h-6 w-3/4 rounded bg-muted" />
              <div className="h-4 w-full rounded bg-muted" />
            </div>
            <div className="h-9 w-28 rounded bg-muted mt-6" />
          </div>)}
      </div>;
  }
  const displayPosts = !error && posts && posts.length > 0 ? posts : null;
  if (displayPosts) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
        {displayPosts.slice(0, 3).map((post, i) => {
        const category = post.categories && post.categories.length > 0 ? post.categories[0].name : post.customFields?.category as string || "Technical Guide";
        const summaryText = post.excerpt || post.customFields?.summary as string || "Detailed technical documentation and engineering reference for stainless steel wire mesh applications.";
        const targetUrl = post.link || "/TechnicalResourcesPage";
        const coverImg = post.featuredImage || post.customFields?.cover_image as string;
        return <Card key={post.id} data-index={i} className="flex flex-col h-full rounded-lg border border-border/80 hover:border-primary/40 transition-colors bg-card overflow-hidden group">
              {coverImg ? <div className="relative mf-card-media mf-ratio-card w-full overflow-hidden">
                  <img src={coverImg} data-wvc-srcset="" data-wvc-sizes="" alt={post.title || "Technical article illustration"} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                  <div className="absolute top-3 left-3">
                    <Badge variant="outline" className="bg-muted text-foreground border border-border font-medium text-[13px]">
                      {category}
                    </Badge>
                  </div>
                </div> : <div className="px-6 pt-6 flex justify-between items-start">
                  <Badge variant="outline" className="bg-muted text-foreground border border-border font-medium text-[13px]">
                    {category}
                  </Badge>
                  <div className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-muted text-primary">
                    <FileText className="size-5" aria-hidden="true" />
                  </div>
                </div>}

              <CardHeader className={coverImg ? "pt-5 px-6 pb-2" : "pb-2 px-6"}>
                <CardTitle className="mf-h3 text-foreground group-hover:text-primary transition-colors line-clamp-2">
                  {post.title}
                </CardTitle>
              </CardHeader>

              <CardContent className="flex-1 px-6 py-2">
                <p className="text-sm text-muted-foreground line-clamp-3 leading-relaxed">
                  {summaryText}
                </p>
              </CardContent>

              <CardFooter className="px-6 pb-6 pt-4 border-t border-border/40 flex items-center justify-between">
                {post.date && <div className="flex items-center text-[13px] text-muted-foreground gap-1.5">
                    <SafeHydrate><Calendar className="size-3.5" /></SafeHydrate>
                    <span>{new Date(post.date).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric"
                })}</span>
                  </div>}
                <Button asChild variant="ghost" className="ml-auto text-primary hover:text-primary">
                  <Link to={targetUrl}>
                    Read article
                    <ArrowRight className="size-4 ml-1" aria-hidden="true" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>;
      })}
      </div>;
  }
  return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
      {fallbackArticles.map((article, i) => <Card key={article.id} data-index={i} className="flex flex-col h-full rounded-lg border border-border/80 hover:border-primary/40 transition-colors bg-card overflow-hidden group">
          <div className="relative mf-card-media mf-ratio-card w-full overflow-hidden">
            <img src={article.image} data-wvc-srcset={article.imageSrcset} data-wvc-sizes={article.imageSizes} alt={article.title} loading="lazy" decoding="async" className="h-full w-full object-cover" />
            <div className="absolute top-3 left-3">
              <Badge variant="outline" className="bg-muted text-foreground border border-border font-medium text-[13px]">
                {article.category}
              </Badge>
            </div>
          </div>

          <CardHeader className="pt-5 px-6 pb-2">
            <CardTitle className="mf-h3 text-foreground group-hover:text-primary transition-colors line-clamp-2">
              {article.title}
            </CardTitle>
          </CardHeader>

          <CardContent className="flex-1 px-6 py-2">
            <p className="text-sm text-muted-foreground line-clamp-3 leading-relaxed">
              {article.excerpt}
            </p>
          </CardContent>

          <CardFooter className="px-6 pb-6 pt-4 border-t border-border/40 flex items-center justify-between">
            <div className="flex items-center gap-3 text-[13px] text-muted-foreground">
              <span className="flex items-center gap-1">
                <SafeHydrate><Calendar className="size-3.5" /></SafeHydrate>
                {article.date}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="size-3.5" />
                {article.readTime}
              </span>
            </div>
            <Button asChild variant="ghost" className="text-primary hover:text-primary">
              <Link to={article.link}>
                Read guide
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
          </CardFooter>
        </Card>)}
    </div>;
}
export default function RelatedArticles() {
  const wp_query_params = {
    post_type: "technical_document",
    per_page: 3,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="relatedarticles" data-nav="light" className="bg-background text-foreground mf-section border-t border-border/60">
      <div className="mf-container">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 text-primary font-medium text-[13px] mb-3">
              <BookOpen className="size-4" aria-hidden="true" />
              <span>Technical Insights & Compliance</span>
            </div>
            <h2 className="mf-h2 text-foreground text-balance">
              Related technical articles & standards
            </h2>
            <p className="mt-3 text-base text-muted-foreground leading-relaxed">
              In-depth engineering documentation, ISO tolerances, ASTM compliance guides, and material verification procedures from MutualFilter technical specialists.
            </p>
          </div>

          <div className="shrink-0">
            <Button asChild variant="outline" className="font-medium">
              <Link to="/TechnicalResourcesPage">
                View all technical resources
                <ArrowRight className="size-4 ml-2" aria-hidden="true" />
              </Link>
            </Button>
          </div>
        </div>

        <PostsProvider wp_query={{
        post_type: "technical_document",
        per_page: 3,
        order: "desc",
        orderby: "date",
        status: ["publish"]
      }}>
          <RelatedArticlesConsumer />
        </PostsProvider>
      </div>
    </section>;
}